import {
  Box,
  Container,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import LoadingContainer from '@/components/shared/LoadingContainer';
import ProgressBar from '@/components/shared/ProgressBar';
import { constants } from '@/constants';
import useContextAwarenessParams from '@/hooks/useContextAwarenessParams';
import useLandingPageSetup from '@/hooks/useLandingPageSetup';
import usePreloadVideo from '@/hooks/usePreloadVideo';
import useProgress from '@/hooks/useProgress';
import { Page } from '@/lib/tagging';
import useTagging from '@/lib/tagging/useTagging';
import useSessionStore from '@/store/SessionStore';
import type { FlowType } from '@/types/FlowType';
import HelpOptionCard from '../components/HelpOptionCard';
import {
  DEFAULT_SELECTED_OPTION_KEY,
  type HelpOption,
  type HelpOptionKey,
  helpOptions,
} from '../utils/helpOptions';

const HELP_SELECTION_PROGRESS_PERCENT = 10;

const SEARCH_PATH = '/search';

const GRID_COLUMNS = 2;

// WAI-ARIA radiogroup nav: arrows wrap through the 2-column grid, Home/End jump.
type IndexFn = (index: number, length: number, cols: number) => number;
const ARROW_NAVIGATION: Readonly<Record<string, IndexFn>> = {
  ArrowDown: (i, len, cols) => (i + cols) % len,
  ArrowUp: (i, len, cols) => (i - cols + len) % len,
  ArrowRight: (i, len) => (i + 1) % len,
  ArrowLeft: (i, len) => (i - 1 + len) % len,
  Home: () => 0,
  End: (_, len) => len - 1,
};

interface HelpSelectionProps {
  flow: FlowType;
}

const HelpSelection: React.FC<HelpSelectionProps> = ({ flow }) => {
  const navigate = useNavigate();
  const tagging = useTagging();
  const [tagged, setTagged] = useState(false);
  const [selectedKey, setSelectedKey] = useState<HelpOptionKey>(
    DEFAULT_SELECTED_OPTION_KEY,
  );
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);

  const { sessionId, sessionSource, flowType } = useSessionStore();

  // Hook owns context-awareness handling: it stores card/sourceUrl as
  // attribution and, when ?dest= is present, resolves it and navigates
  // straight into scheduling (we render a loader while it resolves).
  const { isResolving } = useContextAwarenessParams(flow);
  useLandingPageSetup(flow);
  useProgress(HELP_SELECTION_PROGRESS_PERCENT);
  // Warm the cache for the next page's hero video so the transition to
  // /search doesn't wait on a cold network fetch.
  usePreloadVideo(constants.RELEVANCE_SEARCH_BG_VIDEO_URL);

  useEffect(() => {
    if (tagged || isResolving) return;
    if (!sessionSource || !sessionId) return;

    tagging.tag('view', sessionSource || 'new', {
      page: Page.startOptions,
      sessionid: sessionId,
      flow: flowType || '',
    });
    setTagged(true);
  }, [tagged, isResolving, sessionSource, sessionId, flowType, tagging.tag]);

  // Programmatic focus on mount: arrow keys work immediately, and since it
  // doesn't trigger :focus-visible, mouse users see no focus ring on load.
  useEffect(() => {
    if (isResolving) return;
    const initialIndex = helpOptions.findIndex(
      (option) => option.key === DEFAULT_SELECTED_OPTION_KEY,
    );
    if (initialIndex < 0) return;
    cardRefs.current[initialIndex]?.focus();
  }, [isResolving]);

  const selectedOption = helpOptions.find(
    (option) => option.key === selectedKey,
  );

  const handleSelectOption = (option: HelpOption) => {
    setSelectedKey(option.key);
  };

  const handleClickContinue = () => {
    if (!selectedOption) return;

    const { action } = selectedOption;
    switch (action.type) {
      case 'flow':
        navigate(SEARCH_PATH);
        return;
      case 'external':
        window.open(action.url, '_blank', 'noopener,noreferrer');
        return;
    }
  };

  const handleRadiogroupKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleClickContinue();
      return;
    }

    const move = ARROW_NAVIGATION[event.key];
    if (!move) return;
    event.preventDefault();

    const currentIndex = helpOptions.findIndex(
      (option) => option.key === selectedKey,
    );
    if (currentIndex < 0) return;

    const nextIndex = move(currentIndex, helpOptions.length, GRID_COLUMNS);
    setSelectedKey(helpOptions[nextIndex].key);
    cardRefs.current[nextIndex]?.focus();
  };

  if (isResolving) {
    return (
      <LoadingContainer
        message='Finding your destination...'
        showRadios={false}
      />
    );
  }

  return (
    <Stack
      direction='column'
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: '#FAF8F4',
      }}
      data-testid='help-selection-page'
    >
      <Box
        component='header'
        sx={{
          flexShrink: 0,
          backgroundColor: 'secondary.300',
          display: 'flex',
          alignItems: 'center',
          height: 64,
          pl: 2,
          boxShadow: '0 20px 40px 0 rgba(26, 28, 25, 0.04)',
        }}
      >
        <img
          src='/travel/agent/scheduler/aaaLogo.svg'
          alt='AAA'
          width={52}
          height={32}
          style={{ display: 'block' }}
        />
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Container maxWidth='md' sx={{ py: 4, px: { xs: 2, sm: 3 } }}>
          <Stack
            spacing={1.5}
            sx={{ mb: 4, textAlign: 'center', alignItems: 'center' }}
          >
            <Typography
              variant='h4'
              sx={{ color: 'grey.800' }}
              data-testid='help-selection-header'
            >
              How can we help you today?
            </Typography>
            <Typography
              variant='b1Regular'
              sx={{ color: 'grey.900' }}
              data-testid='help-selection-subheader'
            >
              Choose an option below to get started
            </Typography>
          </Stack>

          <Box
            role='radiogroup'
            aria-label='Choose how we can help you'
            data-testid='help-selection-options'
            onKeyDown={handleRadiogroupKeyDown}
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${GRID_COLUMNS}, 1fr)`,
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            {helpOptions.map((option, index) => (
              <HelpOptionCard
                key={option.key}
                ref={(node) => {
                  cardRefs.current[index] = node;
                }}
                title={option.title}
                description={option.description}
                imageSrc={option.imageSrc}
                iconBg={option.iconBg}
                imageAlignTopRight={option.imageAlignTopRight}
                selected={selectedKey === option.key}
                tabIndex={selectedKey === option.key ? 0 : -1}
                testId={option.testId}
                onClick={() => handleSelectOption(option)}
              />
            ))}
          </Box>
        </Container>
      </Box>

      <ProgressBar
        buttonDisabled={!selectedOption}
        displayBackButton={true}
        onClickContinue={handleClickContinue}
      />
    </Stack>
  );
};

export default HelpSelection;
